/**
 * Day-granularity date handling for the performance calendar.
 *
 * Windows in this system are *business days in one timezone*, not instants. If
 * an employee in IST submits at 23:30 on 30 Sep, that is still 30 Sep for the
 * purpose of "is the Q2 window open?" - evaluating it in UTC would read as
 * 1 Oct and wrongly reject them. So every window boundary is a PlainDate
 * resolved in the organisation's timezone, while timestamps that record *when
 * something happened* (submittedAt, managerDueAt) stay as real Date instants.
 */

/** The organisation's business timezone. All window boundaries resolve here. */
export const BUSINESS_TIME_ZONE = 'Asia/Kolkata';

/**
 * A calendar date with no time or zone, formatted `YYYY-MM-DD`.
 *
 * Kept as a plain string on purpose: in this format lexicographic order equals
 * chronological order, so every window check is a pair of string comparisons
 * with no parsing and no off-by-one timezone bugs.
 */
export type PlainDate = string;

const PLAIN_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isPlainDate(value: unknown): value is PlainDate {
  if (typeof value !== 'string' || !PLAIN_DATE_RE.test(value)) return false;
  // Reject impossible dates like 2026-02-31 that pass the regex.
  return toPlainDate(parsePlainDate(value)) === value;
}

export function assertPlainDate(value: unknown): PlainDate {
  if (!isPlainDate(value)) {
    throw new TypeError(`Expected a YYYY-MM-DD date, received ${JSON.stringify(value)}`);
  }
  return value;
}

/** Build a PlainDate from calendar parts. `month` is 1-12. */
export function plainDate(year: number, month: number, day: number): PlainDate {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(year).padStart(4, '0')}-${pad(month)}-${pad(day)}`;
}

/** Split a PlainDate into its numeric parts. */
export function plainDateParts(date: PlainDate): { year: number; month: number; day: number } {
  const [y, m, d] = date.split('-');
  return { year: Number(y), month: Number(m), day: Number(d) };
}

/** Interpret a PlainDate as UTC midnight, for arithmetic only. */
export function parsePlainDate(date: PlainDate): Date {
  const { year, month, day } = plainDateParts(date);
  return new Date(Date.UTC(year, month - 1, day));
}

/**
 * The calendar date an instant falls on, in the business timezone.
 *
 * `en-CA` is used because it formats as YYYY-MM-DD natively, which avoids
 * hand-assembling parts from formatToParts.
 */
export function toPlainDate(instant: Date, timeZone: string = BUSINESS_TIME_ZONE): PlainDate {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}

/** Today, in the business timezone. */
export function today(now: Date = new Date(), timeZone: string = BUSINESS_TIME_ZONE): PlainDate {
  return toPlainDate(now, timeZone);
}

export function addDays(date: PlainDate, days: number): PlainDate {
  const d = parsePlainDate(date);
  d.setUTCDate(d.getUTCDate() + days);
  return toPlainDate(d, 'UTC');
}

/** Whole days from `from` to `to`; negative when `to` precedes `from`. */
export function daysBetween(from: PlainDate, to: PlainDate): number {
  const ms = parsePlainDate(to).getTime() - parsePlainDate(from).getTime();
  return Math.round(ms / 86_400_000);
}

/** Inclusive on both ends - windows in this system always include their last day. */
export function isWithin(date: PlainDate, from: PlainDate, to: PlainDate): boolean {
  return date >= from && date <= to;
}

export function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function minPlainDate(a: PlainDate, b: PlainDate): PlainDate {
  return a <= b ? a : b;
}

export function maxPlainDate(a: PlainDate, b: PlainDate): PlainDate {
  return a >= b ? a : b;
}

/** e.g. "24 Sep 2026" - for banners and review headers. */
export function formatPlainDate(date: PlainDate): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(parsePlainDate(date));
}
